import { Head, Link } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { BumpTrainingSubNav } from "@/components/bump_training/BumpTrainingSubNav"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type {
  CouncilCourseColumn,
  CouncilRow,
  PeriodOption,
  SummaryCourseRow,
  SummaryNoneRow,
  SummaryPaging,
} from "@/types/bump_training"

const DESCRIPTION =
  "What Building Union Power training has been delivered across the union, course by course, across the last eight two-week periods."

export default function BumpTrainingSummary({
  periods,
  courses,
  period_totals,
  grand_total,
  none_totals,
  council_courses,
  councils,
  council_totals,
  paging,
}: {
  periods: PeriodOption[]
  courses: SummaryCourseRow[]
  period_totals: number[]
  grand_total: number
  none_totals: SummaryNoneRow | null
  council_courses: CouncilCourseColumn[]
  councils: CouncilRow[]
  council_totals: number[]
  paging: SummaryPaging
}) {
  return (
    <>
      <Head title="BUMP Training Summary">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="BUMP Training Summary" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="BUMP Training Summary"
          description={
            grand_total === 1
              ? "1 training day logged across these periods."
              : grand_total.toLocaleString() + " training days logged across these periods."
          }
          tabs={<BumpTrainingSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-muted">
              {periods[0]?.range} &mdash; {periods[periods.length - 1]?.range}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" asChild>
                <Link href={"/bump_training/summary?period=" + paging.earlier}>
                  <ChevronLeft className="size-4" aria-hidden="true" />
                  Earlier
                </Link>
              </Button>
              <Button variant="secondary" asChild>
                <Link href={"/bump_training/summary?period=" + paging.later}>
                  Later
                  <ChevronRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
              {paging.current ? (
                <Button variant="ghost" asChild>
                  <Link href="/bump_training/summary">This period</Link>
                </Button>
              ) : null}
            </div>
          </div>

          <h2>When it happened</h2>
          <p className="mb-3 mt-1 text-sm text-ink-muted">
            Training days by course, across each two-week period.
          </p>

          {/*
            The table renders its own horizontal scroller, so the grid stays
            inside its box however many periods are on show and the page body
            never scrolls sideways. The course column is sticky, so scrolling
            the periods does not leave a row of anonymous numbers.
          */}
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell sticky>Course</TableHeaderCell>
                {periods.map((period, index) => (
                  <TableHeaderCell key={period.value} numeric>
                    <span className="block">P{period.number}</span>
                    <span className="block font-normal text-ink-muted">
                      {period.range.replace(/, \d{4}$/, "")}
                    </span>
                    <span className="block font-normal text-ink-muted">
                      {period_totals[index]}
                    </span>
                  </TableHeaderCell>
                ))}
                <TableHeaderCell numeric>Total</TableHeaderCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {courses.map((course) => (
                <TableRow key={course.id}>
                  <TableCell sticky nowrap>
                    <span className="font-medium text-ink-display">{course.code}</span>
                    {!course.active && (
                      <Badge tone="muted" className="ml-2">
                        Retired
                      </Badge>
                    )}
                    {course.label !== course.code && (
                      <span className="block text-ink-muted">{course.label}</span>
                    )}
                  </TableCell>
                  {course.cells.map((count, index) => (
                    <TableCell key={periods[index].value} numeric>
                      {count === 0 ? (
                        <span className="text-ink-muted">
                          <span aria-hidden="true">&mdash;</span>
                          <span className="sr-only">
                            No {course.code} delivered in {periods[index].label}
                          </span>
                        </span>
                      ) : (
                        count
                      )}
                    </TableCell>
                  ))}
                  <TableCell numeric>
                    <span className="font-medium text-ink-display">{course.total}</span>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow className="bg-surface">
                <TableCell sticky nowrap>
                  <span className="font-semibold text-ink-display">All courses</span>
                </TableCell>
                {period_totals.map((total, index) => (
                  <TableCell key={periods[index].value} numeric>
                    <span className="font-semibold text-ink-display">{total}</span>
                  </TableCell>
                ))}
                <TableCell numeric>
                  <span className="font-semibold text-ink-display">{grand_total}</span>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {none_totals ? (
            <p className="mt-3 text-sm text-ink-muted">
              A further {none_totals.total.toLocaleString()} day
              {none_totals.total === 1 ? " was" : "s were"} logged as
              &ldquo;None&rdquo; &mdash; days recorded with no course delivered. They are
              kept off the grid so the courses stay comparable, and they still appear on
              each person&apos;s own tracker.
            </p>
          ) : null}

          <section className="mt-10">
            <h2>Who delivered it</h2>
            <p className="mb-3 mt-1 text-sm text-ink-muted">
              The same {periods.length} periods, by council rather than by date. Only
              courses actually delivered in this window get a column.
            </p>

            {councils.length === 0 ? (
              <p className="rounded-md border border-hairline bg-surface px-4 py-6 text-center text-sm text-ink-muted">
                No training was delivered in these periods, so there is nothing to break
                down by council yet.
              </p>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell sticky>Council</TableHeaderCell>
                    {council_courses.map((course, index) => (
                      <TableHeaderCell key={course.id} numeric>
                        <span className="block" title={course.label}>
                          {course.code}
                        </span>
                        <span className="block font-normal text-ink-muted">
                          {council_totals[index]}
                        </span>
                      </TableHeaderCell>
                    ))}
                    <TableHeaderCell numeric>Total</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {councils.map((council) => (
                    <TableRow key={council.id ?? "unassigned"}>
                      <TableCell sticky nowrap>
                        <span className="font-medium text-ink-display">{council.name}</span>
                      </TableCell>
                      {council.cells.map((count, index) => (
                        <TableCell key={council_courses[index].id} numeric>
                          {count === 0 ? (
                            <span className="text-ink-muted">
                              <span aria-hidden="true">&mdash;</span>
                              <span className="sr-only">
                                No {council_courses[index].code} delivered by {council.name}
                              </span>
                            </span>
                          ) : (
                            count
                          )}
                        </TableCell>
                      ))}
                      <TableCell numeric>
                        <span className="font-medium text-ink-display">{council.total}</span>
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow className="bg-surface">
                    <TableCell sticky nowrap>
                      <span className="font-semibold text-ink-display">All councils</span>
                    </TableCell>
                    {council_totals.map((total, index) => (
                      <TableCell key={council_courses[index].id} numeric>
                        <span className="font-semibold text-ink-display">{total}</span>
                      </TableCell>
                    ))}
                    <TableCell numeric>
                      <span className="font-semibold text-ink-display">
                        {council_totals.reduce((sum, n) => sum + n, 0)}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </section>

          <p className="mt-6 text-sm text-ink-muted">
            Every square counts training days across the whole union. Days brought over
            from the old tracker sit alongside the ones logged here.
          </p>
        </div>
      </AppShell>
    </>
  )
}
